<!--
� 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
-->

<section class="section-container">
	<section class="sub-section">
		<div class="sub-section-header">
			<span class="sub-section-title">2. Scenarios</span>

			<span class="sub-section-description">
				Select the uploaded hardware list to compare with each others.
			</span>
		</div>

		@if (isLoading) {
			<div class="loading">
				<globalictcc-spinner />

				<span class="message">Loading...</span>
			</div>
		} @else {
			@if (hasError) {
				<div class="error">
					<div class="error-message">
						<span class="icon-unlink"></span>

						<span class="message">
							There are some errors in the file you uploaded, please upload the file
							in correct form.
						</span>

						<button
							class="error-log-toggle"
							(click)="isErrorLogOpened = !isErrorLogOpened"
						>
							Error log

							<span
								class="icon-chevron-down"
								[ngClass]="{ 'is-opened': isErrorLogOpened }"
							></span>
						</button>

						@if (isErrorLogOpened) {
							<span class="error-log">
								{{ errorLogMessage }}
							</span>
						}

						<globalictcc-button
							label="Retry"
							iconName="update"
							[textOnly]="true"
							(handleClick)="hasError = false"
						/>
					</div>
				</div>
			} @else {
				<div class="scenarios-actions">
					@if (scenariosChips.length) {
						<globalictcc-chips
							[chips]="scenariosChips"
							[removable]="true"
							(handleRemoval)="onScenarioRemove($event)"
						/>
					}

					<globalictcc-button
						iconName="plus"
						iconPosition="left"
						label="Add new scenario"
						(handleClick)="onAddNewScenario()"
					/>
				</div>

				@if (scenariosChips.length) {
					<div class="chart">
						<span class="chart-title">Emission overview</span>

						<div class="type-filter">
							<globalictcc-dropdown
								label="Type"
								[options]="typeOptions"
								[formControl]="typeControl"
							/>

							<globalictcc-button
								label="Apply"
								(handleClick)="getEmissionOverview()"
							/>
						</div>

						<globalictcc-chart
							chartType="barX"
							[isTitleVisible]="false"
							[isLegendVisible]="true"
							[data]="emissionOverviewBarChart?.chart"
							[isLoading]="isEmissionOverviewBarChartLoading"
							[hasError]="emissionOverviewBarChart?.hasError"
							[configOverrides]="totalEmissionByManufacturerBarChartOverrides"
							(handleChartRefresh)="getEmissionOverview()"
						/>
					</div>

					<div class="chart">
						<span class="chart-title">Total emission by category</span>

						<globalictcc-chart
							chartType="barX"
							[isTitleVisible]="false"
							[isLegendVisible]="true"
							[data]="totalEmissionByCategoryBarChart?.chart"
							[isLoading]="isTotalEmissionByCategoryBarChartLoading"
							[hasError]="totalEmissionByCategoryBarChart?.hasError"
							[configOverrides]="totalEmissionByCategoryBarChartOverrides"
							[colors]="totalEmissionByCategoryBarChartColors"
							(handleChartRefresh)="getTotalEmissionByCategory()"
						/>
					</div>

					<div class="chart">
						<span class="chart-title">Total emission by manufacture</span>

						<div class="order-filter">
							<globalictcc-chips
								[chips]="totalEmissionByManufactureChips"
								(handleSelection)="onModeChange($event)"
							/>

							<globalictcc-dropdown
								label="Order by"
								[options]="orderByOptions"
								[formControl]="orderByControl"
								(handleSelectionChange)="onOrderByChange($event)"
							/>
						</div>

						<globalictcc-chart
							chartType="barY"
							[isTitleVisible]="false"
							[isLegendVisible]="true"
							[data]="totalEmissionByManufacturerBarChart?.chart"
							[isLoading]="isTotalEmissionByManufacturerBarChartLoading"
							[hasError]="totalEmissionByManufacturerBarChart?.hasError"
							[configOverrides]="totalEmissionByManufacturerBarChartOverrides"
							(handleChartRefresh)="getTotalEmissionByManufacturer()"
						/>
					</div>
				}
			}
		}
	</section>
</section>

<!-- Dialogs content -->
<ng-template #newScenarioDialogContent>
	<div class="new-scenario-upload-dialog-content">
		<span>Choose from hardware lists or upload a new excel source</span>

		<div class="dialog-table">
			<globalictcc-table
				[gridOptions]="gridOptions"
				(handleTableRefresh)="getFiles()"
			/>
		</div>

		<globalictcc-button
			label="Upload a hardware list"
			iconName="plus"
			[textOnly]="true"
			(handleClick)="onUploadHardwareList()"
		/>
	</div>
</ng-template>

<ng-template #uploadDeviceList>
	<div class="upload-device-list-dialog-content">
		<span>
			Upload your device list in excel format. Use the template as a reference to correctly
			visualize the data.
		</span>

		<globalictcc-upload-file
			[errorMessage]="sameNameErrorMessage()"
			[fluid]="true"
			[acceptedFilesExtensions]="['.xlsx']"
			(handleUpload)="onFileUpload($event)"
		/>

		<globalictcc-checkbox [formControl]="uploadDeviceCheckboxControl">
			Create a new hardware report in hardware page with the uploaded hardware list
		</globalictcc-checkbox>
	</div>
</ng-template>
